import { path } from 'utils'
import { ZodError, z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import {
  layerScopedPathSchema,
  type StreamScopedPathParams,
  streamScopedPathSchema
} from './shared'

type PathParams = StreamScopedPathParams & {
  applicationId: string
  layerId: string
}

type QueryParams = {
  extentType?: 'full' | 'filtered'
  lastRecordId?: number
}

/**
 * Returns the path for fetching extents for a stream, application, and layer,
 * defaulting to the global application and coverage layer.
 *
 * `extentType` defaults to 'filtered', i.e., it excludes pixels
 * that our algorithm has determined are not part of the main task
 * and probably belong to stray seeds dropped at the shed or other outliers.
 */
const pathFor = ({
  orgCode,
  streamId,
  layerId,
  applicationId,
  extentType = 'filtered',
  lastRecordId,
  requestType
}: PathParams & QueryParams) => {
  return path(
    `${requestType}/streams/${orgCode}/${streamId}/layers/${applicationId}/${layerId}/extents`,
    { lastRecordId, filter: extentType === 'filtered' ? true : undefined }
  )
}

const badExtentsMessage = 'Bad extents: expected 5 coordinates'

export const coordinatesSchema = z
  .array(z.array(z.tuple([z.number(), z.number()])).length(5, { message: badExtentsMessage }))
  .length(1, { message: badExtentsMessage })

const responseSchema = z
  .object({
    coordinates: coordinatesSchema,
    type: z.literal('Polygon').optional()
  })
  .refine(
    ({ coordinates }) => coordinates.length === 1 && coordinates[0].length === 5,
    badExtentsMessage
  )

export type Extent = z.infer<typeof responseSchema>

/**
 * The API for fetching extents data from the Stitch API.
 *
 * Represents the bounding box for the field in the given stream and application.
 * It is scoped to the a given layer, but Coverage should always be the most complete.
 */
export const extentsApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: z.object({
    extentType: z.enum(['full', 'filtered']).optional(),
    lastRecordId: z.number().optional()
  }),
  pathFor,
  name: 'GetLayerExtents',
  description: 'Get the geographic bounding box (extents) for a specific layer in a stream',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema
})

export const topLevelExtentsApi = defineApi({
  pathSchema: streamScopedPathSchema,
  querySchema: z.object({
    lastRecordId: z.number().optional()
  }),
  pathFor: (params) => {
    return path(`${params.requestType}/streams/${params.orgCode}/${params.streamId}/extents`, {
      lastRecordId: params.lastRecordId
    })
  },
  name: 'GetStreamExtents',
  description: 'Get the overall geographic bounding box for the entire stream',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema
})

/**
 * Checks if an error is due to a "bad extents," i.e. has coordinates that do not match the expected
 * format of a bounding box with 5 coordinates.
 *
 * This will return true of the parse error includes a bad extents error anywhere inside it, even if
 * other parts of the schema also failed.
 */
export const isBadExtentsError = (error: unknown): error is ZodError =>
  error instanceof ZodError && error.issues.some((e) => e?.message === badExtentsMessage)
